import React from "react"
import {Box, Chip, Typography} from "@mui/material"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import {formatDistanceToNow} from "date-fns"

import type {UnidentifiedMessage, UnidentifiedStatus} from "../types"

import {colors} from "@indebted/theme/colors"

type ChipTint = {bgcolor: string; borderColor: string; color: string; fontWeight?: number}

// The colours separate the reasons at a glance; they are not a severity order,
// so there is nothing to keep in step if a reason is added or dropped.
const deferralReasons: Record<string, {label: string; tint: ChipTint}> = {
	NoMatch: {
		label: "No matching customer",
		tint: {
			bgcolor: colors.spaceBattleBlue["25"],
			borderColor: colors.spaceBattleBlue["200"],
			color: colors.spaceBattleBlue["700"],
			fontWeight: 500,
		},
	},
	MultipleCandidates: {
		label: "Several possible customers",
		tint: {
			bgcolor: colors.blue["50"],
			borderColor: colors.blue["200"],
			color: colors.blue["700"],
			fontWeight: 500,
		},
	},
	AmbiguousSender: {
		label: "Sender address is ambiguous",
		tint: {
			bgcolor: colors.warning["50"],
			borderColor: colors.warning["200"],
			color: colors.warning["800"],
			fontWeight: 500,
		},
	},
	NoSingleRegionOwner: {
		label: "No single region owns this",
		tint: {
			bgcolor: colors.tiffanyBlue["50"],
			borderColor: colors.tiffanyBlue["900"],
			color: "#0F5C57",
			fontWeight: 500,
		},
	},
	ConversationCustomerConflict: {
		label: "Conflicts with the conversation's customer",
		tint: {
			bgcolor: colors.purpleIllusion["50"],
			borderColor: colors.purpleIllusion["900"],
			color: colors.spaceBattleBlue["800"],
			fontWeight: 500,
		},
	},
}

// Tinted apart from the deferral reason beside it: a hint is inferred, not
// decided.
const hintProvenance: Record<string, string> = {
	"web-form": "web form",
}

const hintTint: ChipTint = {
	bgcolor: colors.warmBlue["25"],
	borderColor: colors.warmBlue["200"],
	color: colors.warmBlue["900"],
	fontWeight: 500,
}

type HintLike = {Kind: string; Label: string}

function phraseHint(hint: HintLike) {
	const provenance = hintProvenance[hint.Kind]

	return provenance ? `${hint.Label} · ${provenance}` : hint.Label
}

function HintChip({hint}: {hint: HintLike}) {
	return <Chip label={phraseHint(hint)} size="small" variant="outlined" sx={hintTint} />
}

// The one ground that names somebody else already serving the customer, and so
// the only one the page offers a hand-off on.
const HANDLED_ELSEWHERE = "HandledElsewhere"

const dismissalReasons: Record<string, string> = {
	Junk: "Junk",
	NotACustomer: "Not a customer",
	InternalForward: "Internal forward",
	HandledElsewhere: "Handled elsewhere",
	HandledInOtherRegion: "Handled in the other region",
	Other: "Other",
}

const dismissalReasonIDs = Object.keys(dismissalReasons)

function phraseDismissalReason(reason: string) {
	return dismissalReasons[reason] ?? reason
}

const grayTint: ChipTint = {
	bgcolor: colors.gray["50"],
	borderColor: colors.gray["300"],
	color: colors.gray["700"],
}

const unnamedReasonTint = {...grayTint, fontWeight: 500}

// Drives the colour test, so a reason added above is covered without the test
// having to be told about it.
const styledDeferralReasons = Object.keys(deferralReasons)

function phraseDeferralReason(reason: string) {
	return deferralReasons[reason]?.label ?? reason
}

function DeferralReasonChip({reason}: {reason: string}) {
	return (
		<Chip
			label={phraseDeferralReason(reason)}
			size="small"
			variant="outlined"
			sx={deferralReasons[reason]?.tint ?? unnamedReasonTint}
		/>
	)
}

function SourceChip({label}: {label: string}) {
	return <Chip label={label} size="small" variant="outlined" sx={grayTint} />
}

// A handed-off message is dismissed too, but naming the reason would say the
// queue closed on it when the thread in fact went on to somebody.
function StatusChip({status, message}: {status: UnidentifiedStatus; message: UnidentifiedMessage}) {
	if (status === "assigned") return <Chip label="Assigned" size="small" variant="outlined" color="success" />
	if (status === "dismissed") {
		const dismissal = message.Resolution?.Status === "dismissed" ? message.Resolution : null
		const label = dismissal?.HandedOff
			? "Handed off"
			: dismissal?.Reason
				? `Dismissed · ${phraseDismissalReason(dismissal.Reason)}`
				: "Dismissed"

		return <Chip label={label} size="small" variant="outlined" sx={grayTint} />
	}
	return null
}

function ReceivedAgo({receivedAt}: {receivedAt: string}) {
	return (
		<Box display="flex" alignItems="center" gap={1} color="text.secondary">
			<AccessTimeIcon sx={{fontSize: 20}} />
			<Typography variant="body2" component="span" color="textSecondary">
				<Box component="span" color={colors.gray["700"]}>
					Received
				</Box>{" "}
				{formatDistanceToNow(new Date(receivedAt), {addSuffix: true})}
			</Typography>
		</Box>
	)
}

export {
	DeferralReasonChip,
	dismissalReasonIDs,
	HANDLED_ELSEWHERE,
	HintChip,
	phraseHint,
	phraseDeferralReason,
	phraseDismissalReason,
	ReceivedAgo,
	SourceChip,
	StatusChip,
	styledDeferralReasons,
}
